"use client";
// Site/src/components/mobile/MobileQrModal.tsx
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { parkopplingUrSvar, sattParkoppling, uppdateraParkoppling } from "@/lib/parkoppling-store";
import { MOBIL_PAR_STANG_MS, MOBIL_PAR_STATUS_MS } from "@/lib/klient-intervall";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Pairing {
  pairingId: string;
  qrUrl: string;
  expiresAt: string;
}

type Orsak = "skola" | "klass" | "lektion";

type ViewState =
  | { kind: "loading" }
  | { kind: "connected"; lastSeenAt: string; busy: boolean }
  | { kind: "ready"; pairing: Pairing; status: "pending" | "active"; replaced: boolean }
  | { kind: "expired" }
  | { kind: "rate_limited"; retryAfter: number }
  | { kind: "blocked"; orsak: Orsak }
  | { kind: "error"; message: string };

// Mobilparning kan vara avstängd på tre nivåer. Eleven ska få veta vilken —
// vem hen ska prata med skiljer sig.
const BLOCKED_RUBRIK: Record<Orsak, string> = {
  skola: "Skolan har stängt av mobilen",
  klass: "Mobilen är inte påslagen för din grupp",
  lektion: "Mobilen är avstängd under lektionen",
};

const BLOCKED_TEXT: Record<Orsak, string> = {
  skola:
    "Din skola har stängt av möjligheten att koppla mobilen till Vektr. Fortsätt på datorn — prata med din lärare om du behöver mobilen.",
  klass:
    "Din lärare har inte slagit på mobilparning för den här gruppen. Fortsätt på datorn — säg till din lärare om du behöver mobilen.",
  lektion:
    "Under den här lektionen får mobilen inte användas som räknare eller kamera. Fortsätt på datorn — säg till din lärare om du behöver mobilen.",
};

export default function MobileQrModal({ open, onClose }: Props) {
  const [view, setView] = useState<ViewState>({ kind: "loading" });
  const pollTimer = useRef<number | null>(null);

  function clearPolling() {
    if (pollTimer.current !== null) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }

  async function checkCurrent() {
    setView({ kind: "loading" });
    clearPolling();
    try {
      const res = await fetch("/api/mobile-pair/current", { cache: "no-store" });
      if (!res.ok) {
        setView({ kind: "error", message: "Kunde inte kontrollera anslutning." });
        return;
      }
      const data = (await res.json()) as { active: boolean; lastSeenAt?: string; enhet?: unknown };
      sattParkoppling(parkopplingUrSvar(data));
      if (data.active && data.lastSeenAt) {
        setView({ kind: "connected", lastSeenAt: data.lastSeenAt, busy: false });
        return;
      }
      await createNew(false);
    } catch {
      setView({ kind: "error", message: "Nätverksfel." });
    }
  }

  async function createNew(replaced = false) {
    setView({ kind: "loading" });
    clearPolling();
    try {
      const res = await fetch("/api/mobile-pair/create", { method: "POST" });
      if (res.status === 429) {
        const data = await res.json();
        setView({ kind: "rate_limited", retryAfter: data.retryAfter ?? 3600 });
        return;
      }
      if (res.status === 403) {
        const data = (await res.json().catch(() => null)) as {
          error?: string;
          orsak?: Orsak;
        } | null;
        if (data?.error === "mobilparning_av") {
          // Äldre svar utan orsak: lektionen är den enda spärr som fanns då.
          setView({ kind: "blocked", orsak: data.orsak ?? "lektion" });
          return;
        }
        setView({ kind: "error", message: "Kunde inte skapa QR-kod." });
        return;
      }
      if (!res.ok) {
        setView({ kind: "error", message: "Kunde inte skapa QR-kod." });
        return;
      }
      const pairing = (await res.json()) as Pairing;
      setView({ kind: "ready", pairing, status: "pending", replaced });
      startPolling(pairing.pairingId);
    } catch {
      setView({ kind: "error", message: "Nätverksfel." });
    }
  }

  async function disconnect() {
    setView((prev) => (prev.kind === "connected" ? { ...prev, busy: true } : prev));
    try {
      await fetch("/api/mobile-pair/disconnect", { method: "POST" });
    } catch {
      // ignore — closing anyway
    }
    sattParkoppling({ active: false, enhet: null });
    onClose();
  }

  function startPolling(pairingId: string) {
    pollTimer.current = window.setInterval(async () => {
      // Each status read is a query on the singleton. Nobody scans a QR code in a
      // hidden tab, and once the pairing is active or expired there is nothing left
      // to wait for (prestanda-rattningslatens.md §10b P3: an open modal polled
      // every 2 s for as long as it stayed open).
      if (document.hidden) return;
      try {
        const res = await fetch(
          `/api/mobile-pair/status?pairingId=${encodeURIComponent(pairingId)}`,
          { cache: "no-store" }
        );
        if (!res.ok) return;
        const data = (await res.json()) as { status: string };
        if (data.status === "active" || data.status === "expired") clearPolling();
        setView((prev) => {
          if (prev.kind !== "ready") return prev;
          if (data.status === "active") return { ...prev, status: "active" };
          if (data.status === "expired") return { kind: "expired" };
          return prev;
        });
      } catch {
        // ignore
      }
    }, MOBIL_PAR_STATUS_MS);
  }

  useEffect(() => {
    if (!open) {
      clearPolling();
      return;
    }
    checkCurrent();
    return clearPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (view.kind !== "ready" || view.status !== "active") return;
    // Enheten rapporterar mobil/platta i sin första heartbeat — läs in det så
    // uppgiftsknapparna byter direkt, inte först vid nästa 30 s-poll. En
    // andra läsning efter stängningen fångar en heartbeat som kom sent.
    void uppdateraParkoppling();
    const id = window.setTimeout(() => {
      onClose();
      void uppdateraParkoppling();
    }, MOBIL_PAR_STANG_MS);
    return () => window.clearTimeout(id);
  }, [view, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  // Portal to body so the modal escapes the Nav (z-30) stacking context — its
  // z-50 backdrop then covers the header and impersonation banner consistently.
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Koppla mobil eller läsplatta"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-xl bg-[var(--color-bg-card)] p-6 shadow-xl border border-[var(--color-border)]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--color-primary)]">
            {view.kind === "connected"
              ? "Enhet ansluten"
              : view.kind === "blocked"
                ? "Mobilen"
                : "Koppla mobil eller läsplatta"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Stäng"
            className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col items-center text-center gap-4">
          {view.kind === "loading" && (
            <p className="text-sm text-[var(--color-text-muted)]">Laddar…</p>
          )}
          {view.kind === "connected" && (
            <>
              <p className="text-sm text-[var(--color-success)] font-medium">
                ✓ Din mobil eller läsplatta är redan kopplad
              </p>
              <p className="text-xs text-[var(--color-text-muted)]">
                Du kan koppla bort den från datorn, eller fortsätta använda den.
              </p>
              <div className="flex flex-col gap-2 w-full">
                <button
                  type="button"
                  onClick={disconnect}
                  disabled={view.busy}
                  className="px-4 py-2 rounded-md border border-[var(--color-accent)] text-[var(--color-accent)] text-sm font-medium hover:bg-[var(--color-accent)]/10 transition-colors disabled:opacity-50"
                >
                  Koppla bort enheten
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-md text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                >
                  Avbryt
                </button>
              </div>
            </>
          )}
          {view.kind === "ready" && (
            <>
              <div className="bg-white p-3 rounded-md border border-[var(--color-border)]">
                <QRCodeSVG value={view.pairing.qrUrl} size={224} level="M" />
              </div>
              <p className="text-sm text-[var(--color-text-muted)]">
                Skanna med mobilens eller läsplattans kamera. QR är giltig i 5 min. Mobilen blir räknare och kamera, läsplattan blir din rityta.
              </p>
              {view.replaced && (
                <p className="text-xs text-[var(--color-text-muted)]">
                  En tidigare QR har ersatts av denna.
                </p>
              )}
              {view.status === "pending" && (
                <p className="text-sm text-[var(--color-text-muted)]">Väntar på enheten…</p>
              )}
              {view.status === "active" && (
                <p className="text-sm text-[var(--color-success)] font-medium">✓ Enhet ansluten</p>
              )}
            </>
          )}
          {view.kind === "expired" && (
            <>
              <p className="text-sm text-[var(--color-text-muted)]">QR-koden har gått ut.</p>
              <button
                type="button"
                onClick={() => createNew(true)}
                className="px-4 py-2 rounded-md bg-[var(--color-accent)] text-white text-sm font-medium"
              >
                Skapa ny QR
              </button>
            </>
          )}
          {view.kind === "blocked" && (
            <>
              <div
                aria-hidden="true"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-border)]/60"
              >
                <svg
                  className="h-6 w-6 text-[var(--color-text-muted)]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.75}
                >
                  <rect x="7" y="2" width="10" height="20" rx="2" ry="2" />
                  <line
                    x1="4"
                    y1="21"
                    x2="20"
                    y2="3"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="text-sm font-medium text-[var(--color-text)]">
                {BLOCKED_RUBRIK[view.orsak]}
              </p>
              <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">
                {BLOCKED_TEXT[view.orsak]}
              </p>
              <button
                type="button"
                onClick={onClose}
                title="Stäng och fortsätt på datorn"
                className="px-4 py-2 rounded-md border border-[var(--color-border)] text-sm text-[var(--color-text)] hover:border-[var(--color-text-muted)] transition-colors"
              >
                Okej
              </button>
            </>
          )}
          {view.kind === "rate_limited" && (
            <p className="text-sm text-[var(--color-text-muted)]">
              För många försök. Försök igen om en stund.
            </p>
          )}
          {view.kind === "error" && (
            <p className="text-sm text-[var(--color-accent)]">{view.message}</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
